import UploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, MenuItem, Paper, Select, SelectChangeEvent, Stack, Tooltip, Typography } from "@mui/material";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { ToastContent } from "../../../../../shared/components";
import { ModalPreviewDocument } from "../../../../../shared/components/ModalPreviewDocument/ModalPreviewDocument";
import { LoadingComp } from "../../../../../shared/components/loading/Loading";
import { useToast } from "../../../../../shared/hooks";
import { DocumentsService } from "../../../../../shared/services/api/documents/DocumentsService";
import { GetDocumentTypesResponseType } from "../../../../../shared/services/api/documents/types";
import { ProposalDocumentsService } from "../../../../../shared/services/api/proposal/ProposalDocumentsService";
import { ProposalDocumentType } from "../../../../../shared/services/api/proposal/types/proposal-document.type";
import { formatAnalysisContractTypeEnum } from "../../../../../enums/analysis-documents";
import { returnMessageError } from "../../../../../shared/utils/returnMessageError";

type ProposalFilesModalContentPropsType = {
  proposalId: string;
  onClose?: () => void;
};

interface ModalUploadProposalDocumentProps {
  isOpen: boolean;
  onClose: () => void;
  onAttach: (file: File, documentTypeId: string) => void;
  documentTypes: GetDocumentTypesResponseType[];
}

const normalizeFileName = (fileName: string): string => {
  return fileName
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9._]/g, "");
};

const ModalUploadProposalDocument: React.FC<ModalUploadProposalDocumentProps> = ({ isOpen, onClose, onAttach, documentTypes }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [documentTypeId, setDocumentTypeId] = useState<string>("");
  const supportedFormats = ["png", "jpg", "jpeg", "doc", "docx", "pdf", "odt"];

  useEffect(() => {
    if (!isOpen) {
      setSelectedFile(null);
      setDocumentTypeId("");
      setError(null);
    }
  }, [isOpen]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const fileExtension = file.name.split(".").pop()?.toLowerCase();

      if (!fileExtension || !supportedFormats.includes(fileExtension)) {
        setError(`Formato inválido. Permitidos: ${supportedFormats.join(", ").toUpperCase()}`);
        setSelectedFile(null);
        return;
      }

      const normalized = normalizeFileName(file.name);
      const normalizedFile = new File([file], normalized, { type: file.type });
      setSelectedFile(normalizedFile);
      setError(null);
    }
  };

  const handleAttach = () => {
    if (!documentTypeId) {
      setError("Selecione o tipo de documento.");
      return;
    }

    if (selectedFile) {
      onAttach(selectedFile, documentTypeId);
    }
  };

  const handleClose = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    setTimeout(() => {
      onClose();
    }, 0);
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Anexar documento</DialogTitle>
      <DialogContent>
        {error && (
          <Typography variant="body2" sx={{ color: "#e36b5b", bgcolor: "#fcedea", p: 1, borderRadius: 2, mb: 2 }}>
            {error}
          </Typography>
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Tipo de Documento
          </Typography>
          <Select fullWidth value={documentTypeId} onChange={(e: SelectChangeEvent) => setDocumentTypeId(e.target.value)} displayEmpty>
            <MenuItem value="" disabled>
              Selecione um tipo
            </MenuItem>
            {documentTypes.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.description}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {selectedFile ? (
          <Box sx={{ border: "2px dashed #FFD700", borderRadius: 2, p: 4, mt: 2, textAlign: "center" }}>
            <Typography variant="body1" sx={{ wordBreak: "break-word", textAlign: "center" }}>
              {selectedFile.name}
            </Typography>
            <Button color="error" startIcon={<DeleteIcon />} onClick={() => setSelectedFile(null)}>
              Apagar
            </Button>
          </Box>
        ) : (
          <Box
            sx={{ border: "2px dashed #FFD700", borderRadius: 2, p: 4, mt: 2, cursor: "pointer", textAlign: "center" }}
            onClick={() => document.getElementById("upload-proposal-input")?.click()}
          >
            <UploadIcon sx={{ fontSize: 48, color: "#666", mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Clique para carregar o arquivo (máx. 50MB)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Tipos: PNG, JPG, JPEG, DOC, DOCX, PDF, ODT
            </Typography>
          </Box>
        )}

        <input id="upload-proposal-input" type="file" style={{ display: "none" }} onChange={handleFileChange} />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button onClick={handleAttach} disabled={!selectedFile || !documentTypeId || !!error} variant="contained">
          Anexar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const ProposalFilesModalContent = ({ proposalId, onClose }: ProposalFilesModalContentPropsType) => {
  const [documents, setDocuments] = useState<ProposalDocumentType[]>([]);
  const [documentTypes, setDocumentTypes] = useState<GetDocumentTypesResponseType[]>([]);
  const [previewDoc, setPreviewDoc] = useState<ProposalDocumentType | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const toastHandler = useToast();

  const fetchDocuments = async () => {
    setIsLoading(true);
    try {
      const result = await ProposalDocumentsService.getProposalDocuments(proposalId);
      setDocuments(result);
    } catch (error) {
      const { titleMessage, contentMessage } = returnMessageError(error);
      toastHandler.present({
        type: "error",
        position: "top-right",
        durationMs: 6000,
        messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDocumentTypes = async () => {
    try {
      const result = await DocumentsService.getProposalDocumentTypes();
      setDocumentTypes(result);
    } catch (error) {
      const { titleMessage, contentMessage } = returnMessageError(error);
      toastHandler.present({
        type: "error",
        position: "top-right",
        durationMs: 6000,
        messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
      });
    }
  };

  useEffect(() => {
    fetchDocuments();
    fetchDocumentTypes();
  }, [proposalId]);

  const handleUploadFile = async (file: File, documentTypeId: string) => {
    setIsLoading(true);
    try {
      const result = await ProposalDocumentsService.uploadProposalDocument(proposalId, documentTypeId, file);
      if (result instanceof Error) {
        throw result;
      }

      toastHandler.present({
        type: "success",
        position: "top-right",
        durationMs: 6000,
        messageOrContent: <ToastContent title="Documento enviado" content="O documento foi anexado com sucesso." />,
      });

      setIsUploadModalOpen(false);
      await fetchDocuments();
    } catch (error) {
      const { titleMessage, contentMessage } = returnMessageError(error);
      toastHandler.present({
        type: "error",
        position: "top-right",
        durationMs: 6000,
        messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveFile = async (doc: ProposalDocumentType) => {
    setIsLoading(true);
    try {
      const result = await ProposalDocumentsService.removeDocumentByProposal(doc.id);
      if (result instanceof Error) {
        throw result;
      }

      toastHandler.present({
        type: "success",
        position: "top-right",
        durationMs: 6000,
        messageOrContent: <ToastContent title="Documento removido" content="O documento foi removido com sucesso." />,
      });

      await fetchDocuments();
    } catch (error) {
      const { titleMessage, contentMessage } = returnMessageError(error);
      toastHandler.present({
        type: "error",
        position: "top-right",
        durationMs: 6000,
        messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Box sx={{ width: "100%", maxWidth: 860, mx: "auto" }}>
        <Stack spacing={2} sx={{ width: "100%" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", minHeight: 48 }}>
            <Typography variant="h5" fontWeight={300} color="primary.main">
              Documentos Anexados
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider />

          <Box display="flex" justifyContent="end" alignItems="center">
            <Button variant="contained" size="small" startIcon={<UploadFileIcon />} onClick={() => setIsUploadModalOpen(true)}>
              Anexar Documento
            </Button>
          </Box>

          {documents?.length ? (
            <Stack spacing={2} sx={{ width: "100%" }}>
              {documents.map((doc) => (
                <Paper key={doc.id} variant="outlined" sx={{ width: "100%", px: 2, py: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} flex={1}>
                      <InsertDriveFileIcon fontSize="small" sx={{ color: "#999" }} />
                      <Stack spacing={0.5}>
                        <Typography variant="body2" fontWeight={500} lineHeight={1.2}>
                          {doc.originalFilename}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" lineHeight={1}>
                          {format(new Date(doc.uploadedDate), "dd/MM/yyyy HH:mm")}
                        </Typography>
                        <Typography variant="subtitle2" lineHeight={1}>
                          {documentTypes.find((type) => type.id === doc.docType)?.description || formatAnalysisContractTypeEnum(doc.docType)}
                        </Typography>
                      </Stack>
                    </Stack>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Visualizar">
                        <IconButton size="small" onClick={() => {
                          setPreviewDoc(doc);
                          setPreviewUrl(doc.url);
                        }}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Baixar">
                        <IconButton size="small" onClick={() => window.open(doc.url, "_blank", "noopener,noreferrer")}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remover">
                        <IconButton size="small" onClick={() => handleRemoveFile(doc)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2">Nenhum documento anexado.</Typography>
          )}
        </Stack>
      </Box>

      {previewDoc && (
        <ModalPreviewDocument open={!!previewDoc} onClose={() => {
          setPreviewDoc(null);
          setPreviewUrl(null);
        }} previewDoc={previewDoc} previewUrl={previewUrl} isLoading={false} />
      )}

      <ModalUploadProposalDocument isOpen={isUploadModalOpen} onClose={() => setIsUploadModalOpen(false)} onAttach={handleUploadFile} documentTypes={documentTypes} />

      <LoadingComp isopen={isLoading} />
    </>
  );
};
